/**
 * Vectorize Page
 *
 * Configure and launch vectorization/embedding pipelines for project data.
 */

import React, { useState } from 'react';
import { Card, Stack, Flex, Button, Modal, useTheme, spacing } from '@fti/design-system';
import { useVectors, useVectorMutations } from '../../api/vectors';
import { useHierarchy } from '../../context/HierarchyContext';
import { LoadingState } from '../shared/LoadingState';
import { ErrorDisplay } from '../shared/ErrorDisplay';
import { ConfirmModal } from '../shared/ConfirmModal';
import type { Vector } from '../../api/types';
import { RoleGate } from '../shared/RoleGate';
import { GATES } from '../../auth/roles';
import { errorMessage } from '../../api/errors';

const PROVIDERS = ['OpenAI', 'SentenceTransformers'];
const OPENAI_MODELS = ['text-embedding-3-small', 'text-embedding-3-large', 'text-embedding-ada-002'];
const ST_MODELS = ['all-MiniLM-L6-v2', 'all-mpnet-base-v2', 'paraphrase-multilingual-MiniLM-L12-v2'];

function VectorizeFormModal({ isOpen, onClose, onSave }: {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: { provider: string; model: string; dimensions: number; origin: string; labels: string }) => Promise<void>;
}) {
  const { theme } = useTheme();
  const [provider, setProvider] = useState('OpenAI');
  const [model, setModel] = useState('text-embedding-3-small');
  const [dimensions, setDimensions] = useState('1536');
  const [origin, setOrigin] = useState('consolidated');
  const [labels, setLabels] = useState('');

  const textColor = theme.colors.text.primary;
  const mutedColor = theme.colors.text.secondary;
  const borderColor = theme.colors.border.default;

  const [saving, setSaving] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  const inputStyle: React.CSSProperties = {
    width: '100%',
    boxSizing: 'border-box',
    padding: `${spacing.xs} ${spacing.sm}`,
    borderRadius: '4px',
    border: `1px solid ${borderColor}`,
    background: theme.colors.background.paper,
    color: textColor,
    fontSize: '0.875rem',
  };

  const availableModels = provider === 'OpenAI' ? OPENAI_MODELS : ST_MODELS;

  const handleProviderChange = (newProvider: string) => {
    setProvider(newProvider);
    setModel(newProvider === 'OpenAI' ? OPENAI_MODELS[0] : ST_MODELS[0]);
    setDimensions(newProvider === 'OpenAI' ? '1536' : '384');
  };

  const handleSave = async () => {
    setSaving(true);
    setErrorMsg('');
    try {
      await onSave({ provider, model, dimensions: Number(dimensions) || 1536, origin, labels });
    } catch (err: any) {
      setErrorMsg(err?.message || 'Save failed. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <Modal.Header><h3 style={{ margin: 0, color: textColor }}>Configure Vectorization Pipeline</h3></Modal.Header>
      <Modal.Body>
        <Stack gap="sm">
          <div>
            <label style={{ fontSize: '0.8rem', color: mutedColor, display: 'block', marginBottom: '4px' }}>Embedding Provider</label>
            <select style={inputStyle} value={provider} onChange={(e) => handleProviderChange(e.target.value)}>
              {PROVIDERS.map((p) => <option key={p} value={p}>{p}</option>)}
            </select>
          </div>
          <div>
            <label style={{ fontSize: '0.8rem', color: mutedColor, display: 'block', marginBottom: '4px' }}>Model</label>
            <select style={inputStyle} value={model} onChange={(e) => setModel(e.target.value)}>
              {availableModels.map((m) => <option key={m} value={m}>{m}</option>)}
            </select>
          </div>
          <div>
            <label style={{ fontSize: '0.8rem', color: mutedColor, display: 'block', marginBottom: '4px' }}>Dimensions</label>
            <input type="number" value={dimensions} onChange={(e) => setDimensions(e.target.value)} style={inputStyle} />
          </div>
          <div>
            <label style={{ fontSize: '0.8rem', color: mutedColor, display: 'block', marginBottom: '4px' }}>Origin Table</label>
            <select style={inputStyle} value={origin} onChange={(e) => setOrigin(e.target.value)}>
              <option value="consolidated">Consolidated</option>
              <option value="classification">Classification</option>
            </select>
          </div>
          <div>
            <label style={{ fontSize: '0.8rem', color: mutedColor, display: 'block', marginBottom: '4px' }}>Labels (optional)</label>
            <input placeholder="e.g. positive,negative,neutral" value={labels} onChange={(e) => setLabels(e.target.value)} style={inputStyle} />
          </div>
          {errorMsg && <span style={{ color: theme.colors.semantic.error.default, fontSize: '0.75rem' }}>{errorMsg}</span>}
        </Stack>
      </Modal.Body>
      <Modal.Footer>
        <Flex gap="sm" justify="end">
          <Button variant="ghost" size="sm" onClick={onClose}>Cancel</Button>
          <Button variant="solid" size="sm" onClick={handleSave} disabled={saving}>
            {saving ? 'Launching...' : 'Launch Pipeline'}
          </Button>
        </Flex>
      </Modal.Footer>
    </Modal>
  );
}

export function VectorizePage() {
  const { theme } = useTheme();
  const { selectedProject } = useHierarchy();
  const { data: allVectors, loading, error, refetch } = useVectors({ limit: 100 });
  const { createVector, deleteVector } = useVectorMutations();

  const [showAddModal, setShowAddModal] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<Vector | null>(null);
  const [deleteErrorMsg, setDeleteErrorMsg] = useState('');

  const textColor = theme.colors.text.primary;
  const mutedColor = theme.colors.text.secondary;

  const vectors = selectedProject
    ? allVectors?.filter((v) => v.id_project === selectedProject.id)
    : allVectors;

  const handleSaveNew = async (data: { provider: string; model: string; dimensions: number; origin: string; labels: string }) => {
    if (!selectedProject) return;
    await createVector({
      id_project: selectedProject.id,
      active: true,
      origin: data.origin,
      embedding_provider: data.provider,
      embedding: data.model,
      length: data.dimensions,
      model_labels: data.labels || undefined,
    });
    setShowAddModal(false);
    refetch();
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    try {
      await deleteVector(deleteTarget.id);
      setDeleteTarget(null);
      refetch();
    } catch (err: unknown) {
      // Previously `catch { /* error handled */ }` — it was not. A failed delete
      // left the confirmation modal open with no feedback at all.
      setDeleteErrorMsg(errorMessage(err, 'Failed to delete pipeline.'));
    }
  };

  if (!selectedProject) {
    return (
      <Stack gap="md">
        <h2 style={{ margin: 0, fontSize: '1.4rem', fontWeight: 600, color: textColor }}>Vectorize</h2>
        <Card variant="outlined" padding="lg">
          <Flex direction="column" align="center">
            <span style={{ color: mutedColor }}>
              Select a client, matter, and project from the context bar to configure vectorization.
            </span>
          </Flex>
        </Card>
      </Stack>
    );
  }

  if (loading) return <LoadingState message="Loading vectorization pipelines..." />;
  if (error) return <ErrorDisplay error={error} onRetry={refetch} />;

  return (
    <Stack gap="md">
      <Flex justify="between" align="center">
        <div>
          <h2 style={{ margin: 0, fontSize: '1.4rem', fontWeight: 600, color: textColor }}>Vectorize</h2>
          <p style={{ margin: `${spacing.xs} 0 0`, color: mutedColor, fontSize: '0.85rem' }}>
            Project: {selectedProject.name} (v{selectedProject.version})
          </p>
        </div>
        <RoleGate minRole={GATES.hierarchyWrite}>
          <Button variant="solid" size="sm" onClick={() => setShowAddModal(true)}>+ Add Pipeline</Button>
        </RoleGate>
      </Flex>

      {vectors && vectors.length > 0 ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: spacing.sm }}>
          {vectors.map((vector) => (
            <Card key={vector.id} variant="outlined" padding="sm">
              <Flex justify="between" align="center">
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 600, color: textColor, fontSize: '0.95rem' }}>
                    {vector.embedding_provider}: {vector.embedding}
                  </div>
                  <Flex gap="sm" style={{ marginTop: '2px' }}>
                    <span style={{ color: mutedColor, fontSize: '0.8rem' }}>
                      Dimensions: {vector.length}
                    </span>
                    <span style={{ color: mutedColor, fontSize: '0.8rem' }}>
                      Origin: {vector.origin}
                    </span>
                    {vector.model_labels && (
                      <span style={{ color: mutedColor, fontSize: '0.8rem' }}>
                        Labels: {vector.model_labels}
                      </span>
                    )}
                    <span style={{
                      fontSize: '0.75rem',
                      padding: '1px 6px',
                      borderRadius: '3px',
                      background: vector.active ? theme.colors.semantic.success.light + '30' : theme.colors.background.paper,
                      color: vector.active ? theme.colors.semantic.success.default : mutedColor,
                    }}>
                      {vector.active ? 'Active' : 'Inactive'}
                    </span>
                  </Flex>
                </div>
                <RoleGate minRole={GATES.hierarchyWrite}>
                  <Button variant="ghost" size="sm" color="error" onClick={() => setDeleteTarget(vector)}>Remove</Button>
                </RoleGate>
              </Flex>
            </Card>
          ))}
        </div>
      ) : (
        <Card variant="outlined" padding="lg">
          <Flex direction="column" align="center">
            <span style={{ color: mutedColor }}>No vectorization pipelines configured for this project</span>
          </Flex>
        </Card>
      )}

      {/* Add Modal */}
      {showAddModal && (
        <VectorizeFormModal
          isOpen={showAddModal}
          onClose={() => setShowAddModal(false)}
          onSave={handleSaveNew}
        />
      )}

      {/* Delete Confirmation */}
      <ConfirmModal
        isOpen={!!deleteTarget}
        title="Remove Pipeline"
        message={deleteErrorMsg || `Remove the ${deleteTarget?.embedding_provider}: ${deleteTarget?.embedding} pipeline?`}
        confirmLabel={deleteErrorMsg ? 'OK' : 'Remove'}
        variant={deleteErrorMsg ? 'warning' : 'danger'}
        onConfirm={deleteErrorMsg ? () => { setDeleteTarget(null); setDeleteErrorMsg(''); } : handleDeleteConfirm}
        onCancel={() => { setDeleteTarget(null); setDeleteErrorMsg(''); }}
      />
    </Stack>
  );
}
